import { useState, useCallback, useMemo, useEffect } from 'react';
import type { ColumnPinningState, ColumnSizingState } from '@tanstack/react-table';

type PinPosition = 'left' | 'right';

type UseColumnPinningOptions = {
  columnIds: string[];
};

/**
 * Pinned-column boundary — GATE-3 F2 (`Docs/UiNativeWave-Plan.md` polish
 * audit). The original implementation drew this with `box-shadow`
 * (`rgba(0, 0, 0, 0.1)`), which pixel-sampled identical to a plain column
 * hairline in both themes.
 *
 * That wasn't a contrast problem — it was structural. Both grid tables
 * render with `border-collapse: collapse` (see the `<table>` className in
 * `ResultsGrid.tsx` / `TableExplorerGrid.tsx`), and per the CSS2.1
 * collapsing-borders model, table cells do not paint their own `box-shadow`
 * at all. Confirmed directly: swapping the shadow for an unmissable
 * `20px solid red` produced a byte-identical screenshot — zero pixels
 * changed. No box-shadow value, however strong, could ever have rendered
 * here.
 *
 * Fix: a real CSS border, which — unlike box-shadow — is exactly what the
 * collapsing-border model exists to render. Every plain column already
 * carries a 1px `border-r` (the `--grid-rule` hairline, locked at ~4%
 * contrast per PP-08) on this same shared edge; the collapse algorithm
 * resolves conflicting borders on a shared edge by width first, so this
 * wider (2px) explicit border wins outright regardless of what the
 * neighbouring cell declares. It paints in `--divider` — the same token
 * used for the sidebar edge and title-bar underline, so it's already
 * proven to read as a structural boundary in both themes — deliberately
 * NOT `--grid-rule`, so this affordance stays visually distinct from an
 * ordinary column divider rather than just louder.
 *
 * Shared by every sticky-pinned cell/header across both grid components
 * (`ResultsGrid.tsx`, `TableExplorerGrid.tsx`, `GridHeaderRow.tsx`) so the
 * boundary is defined once rather than as three copies of the same style.
 */
export function pinnedColumnBorderStyle(side: PinPosition): { borderLeft?: string; borderRight?: string } {
  return side === 'left'
    ? { borderRight: '2px solid var(--divider)' }
    : { borderLeft: '2px solid var(--divider)' };
}

export function useColumnPinning({ columnIds }: UseColumnPinningOptions) {
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>({
    left: [],
    right: [],
  });

  // Prune stale columns when columnIds change
  useEffect(() => {
    const validIds = new Set(columnIds);
    setColumnPinning((prev) => {
      const left = prev.left?.filter((id) => validIds.has(id)) ?? [];
      const right = prev.right?.filter((id) => validIds.has(id)) ?? [];
      const leftChanged = left.length !== (prev.left?.length ?? 0);
      const rightChanged = right.length !== (prev.right?.length ?? 0);
      if (!leftChanged && !rightChanged) return prev;
      return { left, right };
    });
  }, [columnIds]);

  const pinnedLeftSet = useMemo(
    () => new Set(columnPinning.left ?? []),
    [columnPinning.left],
  );

  const pinnedRightSet = useMemo(
    () => new Set(columnPinning.right ?? []),
    [columnPinning.right],
  );

  const pinColumn = useCallback((id: string, position: PinPosition = 'left') => {
    setColumnPinning((prev) => {
      const left = prev.left ?? [];
      const right = prev.right ?? [];
      // Remove from the other side first
      const newLeft = position === 'left'
        ? (left.includes(id) ? left : [...left, id])
        : left.filter((c) => c !== id);
      const newRight = position === 'right'
        ? (right.includes(id) ? right : [...right, id])
        : right.filter((c) => c !== id);
      return { left: newLeft, right: newRight };
    });
  }, []);

  const unpinColumn = useCallback((id: string) => {
    setColumnPinning((prev) => {
      const left = prev.left ?? [];
      const right = prev.right ?? [];
      const inLeft = left.includes(id);
      const inRight = right.includes(id);
      if (!inLeft && !inRight) return prev;
      return {
        left: inLeft ? left.filter((c) => c !== id) : left,
        right: inRight ? right.filter((c) => c !== id) : right,
      };
    });
  }, []);

  const isColumnPinned = useCallback(
    (id: string): PinPosition | false => {
      if (pinnedLeftSet.has(id)) return 'left';
      if (pinnedRightSet.has(id)) return 'right';
      return false;
    },
    [pinnedLeftSet, pinnedRightSet],
  );

  const getPinnedLeftOffset = useCallback(
    (id: string, columnSizing: ColumnSizingState, defaultWidth: number) => {
      const left = columnPinning.left ?? [];
      const idx = left.indexOf(id);
      if (idx <= 0) return 0;
      let offset = 0;
      for (let i = 0; i < idx; i++) {
        offset += columnSizing[left[i]] ?? defaultWidth;
      }
      return offset;
    },
    [columnPinning.left],
  );

  const getPinnedRightOffset = useCallback(
    (id: string, columnSizing: ColumnSizingState, defaultWidth: number) => {
      const right = columnPinning.right ?? [];
      const idx = right.indexOf(id);
      if (idx < 0) return 0;
      // Sum widths of all right-pinned columns AFTER this one
      let offset = 0;
      for (let i = idx + 1; i < right.length; i++) {
        offset += columnSizing[right[i]] ?? defaultWidth;
      }
      return offset;
    },
    [columnPinning.right],
  );

  const isLastPinnedLeft = useCallback(
    (id: string) => {
      const left = columnPinning.left ?? [];
      return left.length > 0 && left[left.length - 1] === id;
    },
    [columnPinning.left],
  );

  const isFirstPinnedRight = useCallback(
    (id: string) => {
      const right = columnPinning.right ?? [];
      return right.length > 0 && right[0] === id;
    },
    [columnPinning.right],
  );

  return {
    columnPinning,
    onColumnPinningChange: setColumnPinning,
    pinColumn,
    unpinColumn,
    isColumnPinned,
    getPinnedLeftOffset,
    getPinnedRightOffset,
    isLastPinnedLeft,
    isFirstPinnedRight,
  };
}
